import { toast } from "@/hooks/use-toast";
import { getClientPortalBase } from "@/lib/clientPortalPaths";
import { setLoader } from "@/store/slices/commonSlice";
import axios, { AxiosError } from "axios";

const BASE_URL = import.meta.env.VITE_API_URL


// Create axios instance with default config
const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  // Sem isso, uma request que nunca recebe resposta (rede instável até o storage, por ex.) deixava
  // a tela "carregando" pra sempre — o spinner só volta a esconder dentro do catch, que depende da
  // promise do axios rejeitar. 60s é generoso pro upload de arquivos grandes numa conexão ruim, mas
  // ainda assim finito.
  timeout: 60_000,
});

export interface ErrorType {
  code: string;
  msg: string;
  data?: any;
}

export interface PageRes<T> {
  result: T[];
  qtdPages: number;
  total: number;
}

// Setup request interceptor outside the request function
// This ensures it's only registered once
api.interceptors.request.use((config) => {
  // /impersonate mora no prefixo /app/client-auth/ mas quem chama é o agente (auth_token), não o
  // cliente — é a rota que o agente usa pra "virar" o cliente, então precisa do token dele.
  const isImpersonate = config.url?.includes("/client-auth/impersonate/");
  const useClientToken =
    !isImpersonate && (config.url?.includes("/app/client/") || config.url?.includes("/app/client-auth/"));
  const token = useClientToken ? localStorage.getItem("client_token") : localStorage.getItem("auth_token");
  if (token) {
    config.headers.Authorization = `${token}`;
  }
  return config;
});

// Setup response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    // Sessão expirou/token ficou inválido (não é "senha errada" nem link público de uso único —
    // esses nunca mandam Authorization, só uma sessão já autenticada que apanhou 401 depois de já
    // estar logada). Sem isso a tela ficava travada mostrando dado vazio + toast de erro, sem
    // nenhum jeito de sair de lá a não ser recarregar a página manualmente.
    const hadAuthHeader = Boolean((error.config?.headers as any)?.Authorization);
    if (error.response?.status === 401 && hadAuthHeader) {
      const isImpersonate = error.config?.url?.includes("/client-auth/impersonate/");
      const isClientRequest =
        !isImpersonate &&
        (error.config?.url?.includes("/app/client/") || error.config?.url?.includes("/app/client-auth/"));
      if (isClientRequest) {
        localStorage.removeItem("client_token");
        localStorage.removeItem("client_user");
        window.location.href = `${getClientPortalBase()}/login`;
      } else {
        localStorage.removeItem("auth_token");
        localStorage.removeItem("user");
        window.location.href = "/login";
      }
      return Promise.reject(error);
    }

    // Handle axios error responses with type safety
    const errorData = error.response?.data as any;

    // Se a mensagem vier do backend, não mostra o toast pois o backend já mostra
    if (!errorData?.error?.msg && !errorData?.message) {
      if (error.code === "ECONNABORTED") {
        toast({
          title: "Erro",
          description: "A operação demorou demais e foi cancelada. Tente novamente.",
          variant: "destructive",
        });
      } else if (error.message) {
        // Network errors ou outros erros do Axios
        toast({
          title: "Erro",
          description: error.message,
          variant: "destructive",
        });
      } else {
        // Fallback genérico
        toast({
          title: "Erro",
          description: "Ocorreu um erro inesperado",
          variant: "destructive",
        });
      }
    }
    return Promise.reject(error);
  }
);

// Generic function for API requests returning [data, error]
async function apiRequest<T>({
  loaderKey = "default",
  method,
  url,
  data,
  ignoreError,
  params
}: {
  loaderKey: string,
  method: 'get' | 'post' | 'put' | 'patch' | 'delete',
  url: string,
  data?: any,
  ignoreError?: boolean
  params?: any
}): Promise<[T | null, ErrorType | null]> {
  // Show loader
  setLoader(loaderKey, true);

  try {
    // Create request config with params if provided
    const requestConfig: any = {};
    if (params) {
      requestConfig.params = params;
    }

    // Make the request with the appropriate method and config
    let response;
    if (method === 'get') {
      response = await api.get(url, requestConfig);
    } else if (method === 'delete') {
      // axios' delete() takes the body via config.data, not as a positional arg
      if (data !== undefined) requestConfig.data = data;
      response = await api.delete(url, requestConfig);
    } else {
      // post, put, patch: data + config
      response = await api[method](url, data, requestConfig);
    }
    
    // Hide loader after request is complete
    setLoader(loaderKey, false);
    return [response.data, null];
  } catch (error: any) {
    // Hide loader after request fails
    setLoader(loaderKey, false);
    
    // If ignoreError is true, don't process error through interceptor
    if (ignoreError) {
      const errorData = error?.response?.data?.error as ErrorType;
      return [null, errorData];
    }
    
    // For non-ignored errors, the interceptor will handle the toast
    // but we still need to return the error
    


    const errorData = error?.response?.data?.error as ErrorType;

    toast({
      title: "Erro",
      description: errorData?.msg || "Ocorreu um erro inesperado",
      variant: "destructive",
    });

    return [null, errorData];
  }
}

// Export apiRequest function to be used by other services
export { apiRequest };

export default api;
